// frontend/src/components/PlotCard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useI18n } from '../../context/I18nContext';
import { getSpeciesName } from '../../utils/speciesUtils';

const PlotCard = ({ plot, farmId, isSelected, onSelectPlot, onAddDevice }) => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [siteData, setSiteData] = useState({
    crop: null,
    pests: [],
    diseases: [],
    hazards: []
  });
  const [loadingHazards, setLoadingHazards] = useState(true);

  const plotId = plot.site_id || plot.id || plot.plotId || plot.external_id;

  useEffect(() => {
    if (!plotId) return;

    let isMounted = true;
    setLoadingHazards(true);

    axios
      .get(`/api/species/hazards?site_id=${plotId}`, { withCredentials: true })
      .then((res) => {
        if (isMounted && res.data) {
          setSiteData({
            crop: res.data.crop || null,
            pests: res.data.pests || [],
            diseases: res.data.diseases || [],
            hazards: res.data.hazards || []
          });
        }
      })
      .catch((err) => {
        console.error(`Failed to load species hazards for site ${plotId}:`, err);
      })
      .finally(() => {
        if (isMounted) setLoadingHazards(false);
      });

    return () => {
      isMounted = false;
    };
  }, [plotId]);

  const getCropDisplay = () => {
    const cropInput = siteData.crop || plot.cropType || plot.crop_type || plot.crop_name || plot.crop || 'Unspecified';
    const cropName = getSpeciesName(cropInput, t);
    const varietal = siteData.crop?.varietal || plot.varietal || plot.cultivar || plot.crop_varietal;

    if (varietal && !cropName.toLowerCase().includes(varietal.toLowerCase())) {
      return `${cropName} (${varietal})`;
    }
    return cropName;
  };

  /**
   * Summarize hardware devices by status and break out proxy devices separately
   */
  const getDeviceSummary = () => {
    const devicesList = plot.devices || plot.assigned_devices || plot.sensors || [];

    if (!devicesList || devicesList.length === 0) {
      return <span style={{ color: '#94a3b8' }}>None assigned</span>;
    }

    let proxyCount = 0;
    const physicalStatusCounts = {};

    devicesList.forEach((dev) => {
      const isProxy = dev.isProxy || dev.is_proxy || dev.device_type === 'proxy';
      if (isProxy) {
        proxyCount++;
      } else {
        const rawStatus = dev.status ? String(dev.status).trim().toLowerCase() : 'unknown';
        physicalStatusCounts[rawStatus] = (physicalStatusCounts[rawStatus] || 0) + 1;
      }
    });

    const segments = [];

    // Hardware status counts (e.g., 1 active, 2 pending)
    Object.entries(physicalStatusCounts).forEach(([status, count]) => {
      segments.push(`${count} ${status}`);
    });

    // Proxy count segment (e.g., 1 proxy)
    if (proxyCount > 0) {
      segments.push(`${proxyCount} proxy`);
    }

    if (segments.length === 0) {
      return <span style={{ color: '#94a3b8' }}>None assigned</span>;
    }

    return segments.join(' | ');
  };

  const riskLevel = plot.overallRiskLevel || plot.primaryRiskLevel || plot.riskLevel || 'LOW';
  const pestNames = siteData.pests.map((p) => getSpeciesName(p, t));
  const diseaseNames = siteData.diseases.map((d) => getSpeciesName(d, t));

  return (
    <div
      className={`plot-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelectPlot && onSelectPlot(plotId)}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '16px',
        borderRadius: '10px',
        border: isSelected ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {/* Header */}
      <div
        className="plot-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}
      >
        <strong style={{ fontSize: '16px', color: '#0f172a' }}>{plot.name || 'Unnamed Plot'}</strong>
        <span className={`risk-pill risk-pill--${String(riskLevel).toLowerCase()}`}>
          {riskLevel}
        </span>
      </div>

      {/* Grid Details */}
      <div
        className="plot-details"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10px 16px',
          fontSize: '13px',
          color: '#334155',
          marginBottom: '16px'
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <strong>{t('farmSummary.primaryCrop') || 'Crop:'}</strong> {getCropDisplay()}
        </div>

        <div>
          <strong style={{ display: 'block', color: '#64748b', fontSize: '12px' }}>
            {t('farmSummary.monitoredPests') || 'Pests'}
          </strong>
          {loadingHazards ? (
            <span className="text-muted">Loading...</span>
          ) : pestNames.length > 0 ? (
            <span className="monitored-list" style={{ wordBreak: 'break-word' }}>{pestNames.join(', ')}</span>
          ) : (
            <span className="text-muted" style={{ color: '#94a3b8' }}>None tracked</span>
          )}
        </div>

        <div>
          <strong style={{ display: 'block', color: '#64748b', fontSize: '12px' }}>
            {t('farmSummary.monitoredDiseases') || 'Diseases'}
          </strong>
          {loadingHazards ? (
            <span className="text-muted">Loading...</span>
          ) : diseaseNames.length > 0 ? (
            <span className="monitored-list" style={{ wordBreak: 'break-word' }}>{diseaseNames.join(', ')}</span>
          ) : (
            <span className="text-muted" style={{ color: '#94a3b8' }}>None tracked</span>
          )}
        </div>

        {/* Devices Summary Section */}
        <div style={{ gridColumn: '1 / -1', paddingTop: '6px', borderTop: '1px dashed #f1f5f9' }}>
          <strong style={{ display: 'block', color: '#64748b', fontSize: '12px' }}>
            {t('farmSummary.devices') || 'Devices'}
          </strong>
          <span style={{ fontSize: '12px', color: '#334155', fontWeight: 500 }}>
            {getDeviceSummary()}
          </span>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="plot-card-footer" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          type="button"
          className="btn-view-details"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/farm/${farmId}/plot/${plotId}`, { state: { plot } });
          }}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: '#f8fafc',
            color: '#334155',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            whiteSpace: 'nowrap'
          }}
        >
          {t('dashboard.viewPlotDetailButton') || 'View Details'}
        </button>

        <button
          type="button"
          className="btn-add-device"
          onClick={(e) => {
            e.stopPropagation();
            if (onAddDevice) onAddDevice(plot);
          }}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: '#166534',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            whiteSpace: 'nowrap'
          }}
        >
          {t('dashboard.addDeviceButton') || '+ Add Device'}
        </button>
      </div>
    </div>
  );
};

export default PlotCard;
